import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle, } from "react";
import { Tooltip } from "@mui/material";
import { jobsService } from "@/api/services/jobs.service";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/components/common/Toast";
import { useAlert } from "@/hooks/useAlert";
import { AddJobChecklistItemModal } from "./AddJobChecklistItemModal";
import { EditJobChecklistItemModal } from "./EditJobChecklistItemModal";
import styles from "./JobChecklist.module.css";
const EditIcon = () => (_jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "#4B5563", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("path", { d: "M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" }), _jsx("path", { d: "M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" })] }));
const RemoveIcon = () => (_jsxs("svg", { width: "20", height: "20", viewBox: "0 0 20 20", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("circle", { cx: "10", cy: "10", r: "9", stroke: "#9CA3AF", strokeWidth: "1.5" }), _jsx("line", { x1: "6", y1: "10", x2: "14", y2: "10", stroke: "#9CA3AF", strokeWidth: "1.5", strokeLinecap: "round" })] }));
export const JobChecklist = forwardRef(function JobChecklist({ jobId, title, addButtonExternal, items: controlledItems, loading: controlledLoading, onRefetch }, ref) {
    const toast = useToast();
    const toastRef = useRef(toast);
    toastRef.current = toast;
    const { confirm } = useAlert();
    const { isAdmin, isStaff } = useAuth();
    const [localItems, setLocalItems] = useState([]);
    const [localLoading, setLocalLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const isControlled = controlledItems !== undefined;
    const items = isControlled ? controlledItems : localItems;
    const isLoading = isControlled ? (controlledLoading ?? false) : localLoading;
    const fetchChecklist = useCallback(async () => {
        if (!jobId) {
            setLocalItems([]);
            return;
        }
        setLocalLoading(true);
        setError(null);
        try {
            const response = await jobsService.getJobChecklist(jobId);
            setLocalItems(response.data.data ?? []);
        }
        catch (err) {
            setError("Failed to load checklist");
            toastRef.current.error("Failed to load checklist", 3000);
            setLocalItems([]);
        }
        finally {
            setLocalLoading(false);
        }
    }, [jobId]);
    useEffect(() => {
        if (!isControlled && jobId)
            fetchChecklist();
    }, [isControlled, jobId, fetchChecklist]);
    const afterMutation = useCallback(async () => {
        if (isControlled && onRefetch)
            await onRefetch();
        else
            await fetchChecklist();
    }, [isControlled, onRefetch, fetchChecklist]);
    useImperativeHandle(ref, () => ({
        openAddModal: () => setShowAddModal(true),
    }), []);
    const canAddEdit = isAdmin || isStaff;
    const canDelete = isAdmin;
    const handleAdd = async (description) => {
        if (!jobId)
            return;
        try {
            await jobsService.createJobChecklistItem({ jobId, description });
            await afterMutation();
            setShowAddModal(false);
            toast.success("Checklist item added", 3000);
        }
        catch (err) {
            toast.error("Failed to add checklist item", 3000);
            throw err;
        }
    };
    const handleEditSave = async (id, description) => {
        try {
            await jobsService.updateJobChecklistItem(id, {
                description,
                complete: editingItem?.complete ?? false,
            });
            await afterMutation();
            setEditingItem(null);
            toast.success("Checklist item updated", 3000);
        }
        catch (err) {
            toast.error("Failed to update checklist item", 3000);
            throw err;
        }
    };
    const handleToggleComplete = async (item) => {
        try {
            await jobsService.updateJobChecklistItem(item.id, {
                description: item.description,
                complete: !item.complete,
            });
            await afterMutation();
        }
        catch (err) {
            toast.error("Failed to update checklist item", 3000);
        }
    };
    const handleDelete = async (itemId) => {
        const confirmed = await confirm("Are you sure you want to delete this checklist item?", "Delete Checklist Item", { confirmText: "Delete", cancelText: "Cancel" });
        if (!confirmed)
            return;
        try {
            await jobsService.deleteJobChecklistItem(itemId);
            await afterMutation();
            toast.success("Checklist item deleted", 3000);
        }
        catch (err) {
            toast.error("Failed to delete checklist item", 3000);
        }
    };
    if (!jobId) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.emptyState, children: _jsx("p", { children: "No job selected" }) }) }));
    }
    if (isLoading) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.loading, children: "Loading checklist..." }) }));
    }
    if (!isControlled && error) {
        return (_jsx("div", { className: styles.container, children: _jsx("div", { className: styles.emptyState, children: _jsx("p", { children: error }) }) }));
    }
    const addItemButton = (_jsxs("button", { type: "button", className: styles.addButton, onClick: () => setShowAddModal(true), children: [_jsx("span", { className: styles.addButtonIcon, children: "+" }), " Add Item"] }));
    const showAddButton = canAddEdit && !addButtonExternal;
    return (_jsxs("div", { className: styles.container, children: [title ? (_jsxs("div", { className: styles.headerRow, children: [_jsx("span", { className: styles.headerRowLabel, children: title }), showAddButton && addItemButton] })) : (showAddButton && (_jsx("div", { className: styles.addButtonRow, children: addItemButton }))), items.length === 0 ? (_jsx("div", { className: styles.emptyState, children: _jsx("p", { children: canAddEdit
                        ? 'No checklist items yet. Click "+ Add Item" to get started.'
                        : "No checklist items available" }) })) : (_jsx("div", { className: styles.grid, children: items.map((item) => (_jsx(Tooltip, { title: item.description || "", disableHoverListener: !item.description, children: _jsxs("div", { className: `${styles.checklistItem2} ${item.complete ? styles.completed : ""}`, children: [_jsx("label", { className: styles.checkboxWrap, children: _jsx("input", { type: "checkbox", checked: item.complete ?? false, onChange: () => handleToggleComplete(item), disabled: !canAddEdit, className: styles.checkboxInput, "aria-label": `Mark "${item.description}" as ${item.complete ? "incomplete" : "complete"}` }) }), _jsx("span", { className: styles.itemText, children: item.description }), canAddEdit && (_jsx("button", { type: "button", className: styles.removeButton, onClick: () => setEditingItem(item), title: "Edit item", children: _jsx(EditIcon, {}) })), canDelete && (_jsx("button", { type: "button", className: styles.removeButton, onClick: () => handleDelete(item.id), title: "Delete item", children: _jsx(RemoveIcon, {}) }))] }) }, item.id))) })), _jsx(AddJobChecklistItemModal, { isOpen: showAddModal, onClose: () => setShowAddModal(false), onAdd: handleAdd }), _jsx(EditJobChecklistItemModal, { isOpen: !!editingItem, onClose: () => setEditingItem(null), onSave: handleEditSave, item: editingItem })] }));
});
